import { type JSX,useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Stack,
    TextField,
    Tooltip,
    Typography } from '@mui/material';
import { Theme,useTheme } from '@mui/material/styles';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { closeDrawer,setNameInConflict, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { PageInfo, selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { APITenantPostParams, APITenantPutParams } from 'common/services/api/Tenants/TenantApiTypeDefs';
import { postTenant, putTenant } from 'common/services/api/Tenants/Tenants.thunks';
import { PostTenantThunkResponse, PutTenantThunkResponse } from 'common/services/api/Tenants/TenantThunkTypeDefs';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';

interface TenantItem {
    name: string;
    isLicenseRequired: boolean;
    id: string;
    applicationSuiteId: string;
}

interface TenantDrawerProperties {
    tenantNameFieldRef: React.RefObject<HTMLInputElement | null>;
    licenseRequiredFieldRef: React.RefObject<HTMLInputElement | null>;
    appSuite: PageInfo;
}

function useTenantDrawer(
    entityName: string,
    drawerProps: TenantDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<TenantItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    const drawerContext = useDrawerContext<
        TenantItem,
        TenantDrawerProperties,
        APITenantPostParams,
        PostTenantThunkResponse,
        APITenantPutParams,
        PutTenantThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const handleSaveBtnEnabled = useCallback(() => {
        dispatch(setSaveButtonEnabled(!!drawerProps?.tenantNameFieldRef?.current?.value));
    }, [dispatch, drawerProps?.tenantNameFieldRef]);

    const getPostOptions = useCallback((): APITenantPostParams | null => ({
        name: drawerProps.tenantNameFieldRef.current?.value ?? '',
        suiteId: drawerProps.appSuite?.id,
        isLicenseRequired: drawerProps.licenseRequiredFieldRef.current?.checked ?? false
    }), [drawerProps]);

    const getPutOptions = useCallback((): APITenantPutParams | null => ({
        name: drawerProps.tenantNameFieldRef?.current?.value ?? '',
        suiteId: drawerProps.appSuite?.id.toString(),
        tenantId: editingItemInfo?.editingItemId?.toString() ?? ''
    }), [drawerProps, editingItemInfo?.editingItemId]);

    const createSuccessHandler = useCallback((postResult: PostTenantThunkResponse) => {
        dispatch(setNameInConflict(false));
        dispatch(closeDrawer());
        const id = postResult.response.data.tenantId;
        navigate(id);
    }, [dispatch, navigate]);

    const editSuccessHandler = useCallback((putResult: PutTenantThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postTenant, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putTenant, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    return {
        ...drawerContext,
        theme,
        handleSaveBtnEnabled,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    postCallback: () => void;
    drawerProps: TenantDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps?.tenantNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="Tenant Name"
                    placeholder="Type Tenant Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Tenant name already in use
                </Box>
                }
                <FormGroup sx={{ verticalAlign: 'center' }}>
                    <Tooltip title='This will require a License to be linked to entitlements'>
                        <FormControlLabel
                            control={<Checkbox
                                defaultChecked={true}
                                disabled={false}
                                inputRef={drawerProps?.licenseRequiredFieldRef} />}
                            label={
                                <Typography variant="body2" component="div">
                                    Require Licenses?
                                </Typography>
                            }
                        />
                    </Tooltip>
                </FormGroup>
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    id="filled-basic"
                    size="small"
                    label="Application Suite Name"
                    defaultValue={drawerProps?.appSuite?.name}
                    variant="filled"
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    putCallback,
    drawerProps,
    editingItemInfo,
    theme
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    putCallback: () => void;
    drawerProps: TenantDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<TenantItem>['editingItemInfo'];
    theme: Theme;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    inputRef={drawerProps?.tenantNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="Tenant Name"
                    placeholder="Type Tenant Name"
                    defaultValue={editingItemInfo?.editingRow?.name}
                    variant="filled"
                    focused
                    autoFocus={true}
                    autoComplete={'off'}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                />
                {nameInConflict &&
                    <Box component='div'
                        display='inline'
                        sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                        Tenant name already in use
                    </Box>
                }
                <Typography variant="body2" component="div" color={theme.palette.info.light}>
                    This tenant <b>
                        {editingItemInfo?.editingRow?.isLicenseRequired
                            ? 'requires'
                            : 'does not require'}
                    </b> licenses
                </Typography>
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    id="filled-basic"
                    size="small"
                    label="Tenant ID"
                    defaultValue={editingItemInfo?.editingItemId}
                    variant="filled"
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    id="filled-basic"
                    size="small"
                    label="Application Suite Name"
                    defaultValue={drawerProps?.appSuite?.name}
                    variant="filled"
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
}

export function TenantsCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<TenantItem>>) {
    const tenantDrawerProps: TenantDrawerProperties = {
        tenantNameFieldRef: useRef<HTMLInputElement>(null),
        licenseRequiredFieldRef: useRef<HTMLInputElement>(null),
        appSuite: useAppSelector(selectPageInfo) ?? { id: '', name: '', tenantRequiresLicense: false }
    };

    const drawerState = useTenantDrawer(title, tenantDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                postCallback={drawerState.postCallback}
                drawerProps={tenantDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                putCallback={drawerState.putCallback}
                drawerProps={tenantDrawerProps}
                editingItemInfo={editingItemInfo}
                theme={drawerState.theme}
            />
        );
    }

    return <></>;
}
